"use client";

import { paymentsApi } from "@/api/payments";
import { useAuth } from "@/auth/AuthProvider";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import type { Order, Payment, PaymentMethod, Table } from "@/lib/pos-types";
import { cn, formatPrice } from "@/lib/utils";
import { isApiEnabled } from "@/routes/role-routing";
import type { Client } from "@/types/client";
import { PaymentsPrivate } from "@/views/private/PaymentsPrivate";
import { Button as ButtonAnt, Form, Modal } from "antd";
import {
  AlertTriangle,
  ArrowRight,
  Banknote,
  Check,
  CreditCard,
  Heart,
  Percent,
  Printer,
  Receipt,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { ClientSelect } from "../ui/selects/ClientSelect";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { CreditOverLimitModal } from "./CreditOverLimitModal";

const tipOptions = [0, 10, 15, 20];

interface CashierViewProps {
  tables: Table[];
  orders: Order[];
  onPaymentCompleted?: (payment: {
    orderId: string;
    tableId?: string;
    tableNumber?: number;
    clientName?: string;
    subtotal: number;
    tax: number;
    discount: number;
    tip: number;
    total: number;
    method: PaymentMethod;
    customerId?: string;
    discountCode?: string;
    cashAmount?: number;
    cardAmount?: number;
  }) => void;
  onReceiptReprint?: (payment: {
    paymentId: string;
    orderId: string;
    tableNumber: number;
    total: number;
    method: PaymentMethod;
  }) => void;
}

export function CashierView({
  tables,
  orders,
  onPaymentCompleted,
  onReceiptReprint,
}: CashierViewProps) {
  const [selectedTable, setSelectedTable] = useState<Table | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [leftPanelMode, setLeftPanelMode] = useState<"pending" | "history">(
    "pending",
  );
  const [selectedHistoryPayment, setSelectedHistoryPayment] =
    useState<Payment | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("cash");
  const [discount, setDiscount] = useState(0);
  const [discountCode, setDiscountCode] = useState("");
  const [tipPercent, setTipPercent] = useState(0);
  const [cashReceived, setCashReceived] = useState("");
  const [historyPayments, setHistoryPayments] = useState<Payment[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(
    null,
  );
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [creditOverLimitModalOpen, setCreditOverLimitModalOpen] =
    useState<boolean>(false);

  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [customDiscount, setCustomDiscount] = useState("");
  const [customTip, setCustomTip] = useState("");

  const [showCustomDiscount, setShowCustomDiscount] = useState(false);
  const [showCustomTip, setShowCustomTip] = useState(false);

  const [isViewPayment, setIsViewPayment] = useState(false);

  const pendingOrders = useMemo(() => {
    return orders.filter((o) => o.status === "pending-payment");
  }, [orders]);

  const handleSelectOrder = (order: Order) => {
    setLeftPanelMode("pending");
    setSelectedHistoryPayment(null);

    setSelectedOrder(order);

    setSelectedTable(
      order.tableId
        ? (tables.find((t) => t.id === order.tableId) ?? null)
        : null,
    );

    setDiscount(0);
    setDiscountCode("");
    setTipPercent(0);
    setCashReceived("");
    setSelectedCustomerId(null);
    setSelectedClient(null);
    setCreditOverLimitModalOpen(false);
  };

  const subtotal = selectedOrder?.subtotal || 0;
  const discountAmount = Math.round(subtotal * (discount / 100));
  const subtotalWithDiscount = subtotal - discountAmount;
  const tax = selectedOrder?.tax || 0;
  const tip = Math.round((subtotalWithDiscount + tax) * (tipPercent / 100));
  const total = subtotalWithDiscount + tip + tax;
  const cashReceivedNumber = cashReceived ? Number(cashReceived) : NaN;
  const mixedCashAmount =
    paymentMethod === "mixed"
      ? Math.min(
          Math.max(
            Number.isFinite(cashReceivedNumber) ? cashReceivedNumber : 0,
            0,
          ),
          total,
        )
      : 0;
  const mixedCardAmount =
    paymentMethod === "mixed" ? Math.max(total - mixedCashAmount, 0) : 0;
  const change = Number.isFinite(cashReceivedNumber)
    ? cashReceivedNumber - total
    : 0;

  const escapeHtml = (value: string) =>
    value
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");

  const printHtml = (html: string) => {
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.position = "fixed";
    iframe.style.left = "-10000px";
    iframe.style.top = "0";
    iframe.style.width = "800px";
    iframe.style.height = "600px";
    iframe.style.border = "0";
    iframe.style.opacity = "0";
    iframe.style.pointerEvents = "none";

    const cleanup = () => {
      setTimeout(() => {
        iframe.remove();
      }, 1_000);
    };

    document.body.appendChild(iframe);

    const doc = iframe.contentDocument;
    const win = iframe.contentWindow;
    if (!doc || !win) {
      toast.error("No se pudo abrir la impresión");
      cleanup();
      return;
    }

    doc.open();
    doc.write(html);
    doc.close();

    const printNow = () => {
      win.focus();
      win.print();
      cleanup();
    };

    if ("fonts" in doc) {
      void (
        doc as unknown as { fonts: { ready: Promise<unknown> } }
      ).fonts.ready
        .catch(() => null)
        .finally(() => setTimeout(printNow, 50));
      return;
    }

    setTimeout(printNow, 150);
  };

  const buildReceiptHtml = (params: {
    title: string;
    isTakeaway?: boolean;
    tableNumber: string | number;
    zone?: string;
    waiter: string;
    dateText: string;
    items: { productName: string; quantity: number; unitPrice: number }[];
    subtotal: number;
    discount: number;
    tip: number;
    tax: number;
    total: number;
    footer?: string;
    metaRight?: string;
  }) => {
    const linesHtml = params.items
      .map((item) => {
        const qty = Number(item.quantity) || 0;
        const unit = Number(item.unitPrice) || 0;
        const lineTotal = qty * unit;
        return `
          <tr class="item">
            <td class="qty">${qty}x</td>
            <td class="name">${escapeHtml(String(item.productName ?? ""))}</td>
            <td class="amt">${escapeHtml(formatPrice(unit))}</td>
            <td class="amt">${escapeHtml(formatPrice(lineTotal))}</td>
          </tr>
        `;
      })
      .join("");

    const subtotalText = formatPrice(params.subtotal + params.tax);
    const discountText =
      params.discount > 0 ? formatPrice(params.discount) : null;
    const tipText = params.tip > 0 ? formatPrice(params.tip) : null;
    const taxText = formatPrice(params.tax);
    const totalText = formatPrice(params.total);

    return `<!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <title>${escapeHtml(params.title)} - Mesa ${params.tableNumber}${params.zone ? ` (${escapeHtml(params.zone)})` : ""}</title>
        <style>
          @page { margin: 8mm; }
          body {
            font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
            color: #111;
          }
          .receipt {
            width: 72mm;
            max-width: 100%;
          }
          h1 {
            font-size: 14px;
            margin: 0 0 6px 0;
            text-align: center;
          }
          .meta {
            font-size: 11px;
            line-height: 1.35;
            margin-bottom: 8px;
          }
          .metaRow {
            display: flex;
            justify-content: space-between;
            gap: 8px;
          }
          .rule {
            border-top: 1px dashed #333;
            margin: 8px 0;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 11px;
          }
          th,
          td {
            padding: 3px 0;
            vertical-align: top;
          }
          thead th {
            font-weight: 700;
            text-align: left;
            padding-bottom: 4px;
          }
          .separator td {
            border-top: 1px dashed #333;
            padding: 0;
            height: 6px;
          }
          .qty {
            width: 10mm;
          }
          .name {
            width: auto;
            padding-right: 6px;
          }
          .unit {
            width: 18mm;
            text-align: right;
            white-space: nowrap;
          }
          .amt {
            width: 18mm;
            text-align: right;
            white-space: nowrap;
          }
          .totals td {
            padding: 2px 0;
          }
          .totals .label {
            text-align: left;
          }
          .totals .value {
            text-align: right;
            white-space: nowrap;
          }
          .grand {
            font-size: 13px;
            font-weight: 700;
          }
          .center {
            text-align: center;
          }
        </style>
        </head>
        <body>
          <div class="receipt">
            <h1>${escapeHtml(params.title)}</h1>
            <div class="meta">
              <div class="metaRow">
                <div>
                  <strong>${params.isTakeaway ? "Cliente:" : "Mesa:"}</strong>
                  ${escapeHtml(String(params.tableNumber))}
                  ${!params.isTakeaway && params.zone ? ` (${escapeHtml(params.zone)})` : ""}
                </div>
                ${params.metaRight ? `<div>${escapeHtml(params.metaRight)}</div>` : ""}
              </div>
              <div><strong>Mesero:</strong> ${escapeHtml(String(params.waiter ?? ""))}</div>
              <div><strong>Fecha:</strong> ${escapeHtml(params.dateText)}</div>
            </div>
            <div class="rule"></div>
            <table>
              <thead>
                <tr class="header">
                  <th class="qty">Uds</th>
                  <th class="name">Descripción</th>
                  <th class="unit">P. U.</th>
                  <th class="amt">Total</th>
                </tr>
              </thead>
              <tbody>
                ${linesHtml}
              </tbody>
            </table>
            <div class="rule"></div>
            <table class="totals">
              <tbody>
                <tr><td class="label">Subtotal</td><td class="value">${escapeHtml(subtotalText)}</td></tr>
                ${discountText ? `<tr><td class="label">Descuento</td><td class="value">- ${escapeHtml(discountText)}</td></tr>` : ""}
                ${tipText ? `<tr><td class="label">Propina</td><td class="value">${escapeHtml(tipText)}</td></tr>` : ""}
                <tr><td class="label grand">TOTAL</td><td class="value grand">${escapeHtml(totalText)}</td></tr>
              </tbody>
            </table>
            <div class="rule"></div>
            ${params.footer ? `<div class="meta center">${escapeHtml(params.footer)}</div>` : ""}
          </div>
        </body>
      </html>`;
  };
  const handlePrintPreBill = () => {
    if (!selectedOrder || (!selectedTable && !selectedOrder.clientName)) {
      return;
    }

    const now = new Date();

    const html = buildReceiptHtml({
      title: "CUENTA",
      isTakeaway: Boolean(selectedOrder.clientName),
      tableNumber: selectedTable?.number ?? selectedOrder.clientName ?? "",
      zone: selectedTable?.zone,
      waiter: String(selectedOrder.waiter ?? ""),
      dateText: now.toLocaleString(),
      items: selectedOrder.items.map((i) => ({
        productName: String(i.productName ?? ""),
        quantity: Number(i.quantity) || 0,
        unitPrice: Number(i.unitPrice) || 0,
      })),
      subtotal,
      discount: discountAmount,
      tip,
      tax,
      total,
      footer: "Gracias por su visita",
    });

    printHtml(html);

    toast.success("Cuenta enviada a impresión", {
      description: selectedTable
        ? `Mesa ${selectedTable.number}${selectedTable.zone ? ` (${selectedTable.zone})` : ""}`
        : `Para llevar - ${selectedOrder.clientName}`,
    });
  };

  const handleReceiptReprintLocal = (payment: Payment) => {
    const receiptItems = payment.receipt?.items ?? [];
    if (receiptItems.length === 0) {
      toast.error("El pago no tiene detalle para reimprimir");
      return;
    }
    const paidAt = payment.paidAt ? new Date(payment.paidAt) : new Date();
    const tableObj = payment.tableNumber
      ? tables.find((t) => t.number === payment.tableNumber)
      : null;

    const isTakeAway = payment.client_name ? true : false;
    const html = buildReceiptHtml({
      title: "COMPROBANTE",
      isTakeaway: Boolean(isTakeAway),
      tableNumber: isTakeAway ? payment.client_name! : payment.tableNumber!,
      zone: tableObj?.zone,
      waiter: payment.receipt?.waiter ?? "",
      dateText: paidAt.toLocaleString(),
      items: receiptItems.map((i) => ({
        productName: i.productName,
        quantity: i.quantity,
        unitPrice: i.unitPrice,
      })),
      subtotal: payment.subtotal,
      discount: payment.discount,
      tip: payment.tip,
      tax: payment.tax,
      total: payment.total,
      footer: "Gracias por su visita",
      metaRight: `Pago: ${payment.method.toUpperCase()}`,
    });
    printHtml(html);
    toast.success("Comprobante reimpreso", {
      description: `Mesa ${payment.tableNumber}${tableObj?.zone ? ` (${tableObj.zone})` : ""} - ${formatPrice(payment.total)}`,
    });
    onReceiptReprint?.({
      paymentId: payment.id,
      orderId: payment.orderId,
      tableNumber: payment.tableNumber ?? 0,
      total: payment.total,
      method: payment.method,
    });
  };

  const handleReceiptReprintById = async (paymentId: string) => {
    if (!isApiEnabled()) return;
    try {
      const full = await paymentsApi.get(paymentId);
      handleReceiptReprintLocal(full);
    } catch {
      toast.error("No se pudo reimprimir el pago");
    }
  };

  const executePaymentProcess = () => {
    if (!selectedOrder) return;

    onPaymentCompleted?.({
      orderId: selectedOrder.id,
      tableId: selectedTable?.id,
      tableNumber: selectedTable?.number,
      clientName: selectedOrder?.clientName,
      subtotal,
      tax,
      discount: discountAmount,
      tip,
      total,
      method: paymentMethod,
      customerId: selectedCustomerId ?? undefined,
      discountCode: discount > 0 ? discountCode.trim() : undefined,
      cashAmount: paymentMethod === "mixed" ? mixedCashAmount : undefined,
      cardAmount: paymentMethod === "mixed" ? mixedCardAmount : undefined,
    });

    setLeftPanelMode("history");
    setSelectedHistoryPayment(null);

    toast.success("Pago procesado correctamente", {
      description: selectedOrder.clientName
        ? `${selectedOrder.clientName} - ${formatPrice(total)}`
        : `Mesa ${selectedTable?.number}${selectedTable?.zone ? ` (${selectedTable.zone})` : ""} - ${formatPrice(total)}`,
    });

    setSelectedTable(null);
    setSelectedOrder(null);
    setSelectedClient(null);
    setCreditOverLimitModalOpen(false);
    setTipPercent(0);
    setCustomTip("");
    setCustomDiscount("");
    setShowCustomTip(false);
    setShowCustomDiscount(false);

    if (isApiEnabled() && selectedOrder) {
      setTimeout(() => {
        void (async () => {
          try {
            const list = await paymentsApi.list({ limit: 50 });
            setHistoryPayments(list);
            const match = list.find((p) => p.orderId === selectedOrder.id);
            if (match) {
              const full = await paymentsApi.get(match.id);
              setSelectedHistoryPayment(full);
            }
          } catch {
            // ignore
          }
        })();
      }, 500);
    }
  };

  const handlePayment = () => {
    if (!selectedOrder) {
      return;
    }

    if (discount > 0 && !discountCode.trim()) {
      toast.error("Se requiere código/ autorización para aplicar descuento");
      return;
    }

    if (paymentMethod === "credit") {
      if (!selectedCustomerId || !selectedClient) {
        toast.error(
          "Debe seleccionar un cliente para realizar un pago a crédito.",
        );
        return;
      }

      if (!selectedClient.credit_enabled) {
        toast.error("El cliente seleccionado no tiene habilitado el crédito.");
        return;
      }

      const creditLimit = Number(selectedClient.credit_limit) || 0;
      const creditUsed = Number(selectedClient.credit_balance) || 0;
      const projectedUsed = creditUsed + total;

      if (creditLimit > 0 && projectedUsed > creditLimit) {
        if (isAdmin) {
          setCreditOverLimitModalOpen(true);
          return;
        } else {
          toast.error(
            `El saldo a crédito (${formatPrice(projectedUsed)}) superará el límite del cliente (${formatPrice(creditLimit)}). Solo un Administrador puede autorizar sobregiros.`,
          );
          return;
        }
      }
    }

    executePaymentProcess();
  };

  return (
    <>
      {isViewPayment ? (
        <>
          <div>
            <ButtonAnt
              onClick={() => {
                setLeftPanelMode("pending");
                setIsViewPayment(false);
                setIsViewPayment(false);
                setSelectedHistoryPayment(null);
              }}
              className="mb-3"
            >
              <ArrowLeftOutlined />
              Volver a pendientes
            </ButtonAnt>
          </div>

          <PaymentsPrivate filterToday={true} />
        </>
      ) : (
        <div className="flex flex-col md:flex-row h-full gap-4 md:gap-6 overflow-hidden">
          {/* Left: Pending Tables List - Hidden on mobile if a table is selected */}
          <div
            className={cn(
              "w-full md:w-[35%] lg:w-[32%] md:min-w-[340px] lg:min-w-[360px] md:max-w-[520px] flex flex-col bg-card rounded-xl border border-border shrink-0 transition-all min-h-0",
              selectedTable && "hidden md:flex",
            )}
          >
            <div className="px-4 py-3 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-foreground">
                  {leftPanelMode === "pending"
                    ? "Mesas Pendientes"
                    : "Historial de Pagos"}
                </h3>
              </div>
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  className={cn(
                    "flex-1 py-1.5 rounded-md text-xs font-medium border transition-colors",
                    leftPanelMode === "pending"
                      ? "bg-primary/10 border-primary text-primary"
                      : "bg-muted/30 border-border text-muted-foreground hover:text-foreground",
                  )}
                  onClick={() => {
                    setLeftPanelMode("pending");
                    setIsViewPayment(false);

                    setSelectedHistoryPayment(null);
                  }}
                >
                  Pendientes ({pendingOrders.length})
                </button>
                <button
                  type="button"
                  className={cn(
                    "flex-1 py-1.5 rounded-md text-xs font-medium border transition-colors",
                    leftPanelMode === "history"
                      ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
                      : "bg-muted/30 border-border text-muted-foreground hover:text-foreground",
                  )}
                  onClick={() => {
                    setIsViewPayment(true);
                    setLeftPanelMode("history");
                    setSelectedTable(null);
                    setSelectedOrder(null);
                  }}
                >
                  Historial
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {leftPanelMode === "pending"
                  ? `${pendingOrders.length} órdenes por cobrar`
                  : historyLoading
                    ? "Cargando historial..."
                    : historyError
                      ? historyError
                      : `${historyPayments.length} mesas pagadas (últimas)`}
              </p>
            </div>

            <ScrollArea className="flex-1 min-h-0">
              <div className="p-2 space-y-2">
                {leftPanelMode === "pending"
                  ? pendingOrders.map((order) => {
                      return (
                        <button
                          key={order.id}
                          onClick={() => handleSelectOrder(order)}
                          className={cn(
                            "w-full flex items-center justify-between p-3 rounded-lg transition-colors text-left",
                            selectedTable?.id === order.id
                              ? "bg-primary/10 border border-primary"
                              : "bg-muted/30 border border-transparent hover:bg-muted/50",
                          )}
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={cn(
                                "flex items-center justify-center w-10 h-10 rounded-lg text-lg font-bold",
                                order.status === "pending-payment"
                                  ? "bg-violet-500/20 text-violet-400"
                                  : "bg-amber-500/20 text-amber-400",
                              )}
                            >
                              {order.clientName
                                ? order.clientName.charAt(0).toUpperCase()
                                : order.tableNumber}
                            </div>

                            <div className="min-w-0 flex flex-col">
                              <span className="font-bold text-foreground">
                                {order.clientName
                                  ? order.clientName
                                  : `Mesa ${order.tableNumber}`}
                              </span>
                              {!order.clientName &&
                                (() => {
                                  const tableObj =
                                    tables.find(
                                      (t) => t.id === order.tableId,
                                    ) ||
                                    tables.find(
                                      (t) => t.number === order.tableNumber,
                                    );
                                  return tableObj?.zone ? (
                                    <span className="text-[11px] text-muted-foreground font-normal leading-tight mt-0.5">
                                      {tableObj.zone}
                                    </span>
                                  ) : null;
                                })()}
                              <p className="text-xs text-muted-foreground truncate">
                                {order.waiter}
                              </p>
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <span className="font-semibold text-foreground whitespace-nowrap">
                              {order ? formatPrice(order.total) : "-"}
                            </span>
                            <Badge
                              variant="outline"
                              className={cn(
                                "text-[10px] ml-2 whitespace-nowrap",
                                order.status === "pending-payment"
                                  ? "bg-violet-500/10 text-violet-400 border-violet-500/30"
                                  : "bg-amber-500/10 text-amber-400 border-amber-500/30",
                              )}
                            >
                              {order.status === "pending-payment"
                                ? "Por pagar"
                                : "Ocupada"}
                            </Badge>
                          </div>
                        </button>
                      );
                    })
                  : null}
              </div>
            </ScrollArea>
          </div>

          {/* Right: Payment Panel - Full width on mobile when a table is selected */}
          <div
            className={cn(
              "flex-1 flex flex-col bg-card rounded-xl border border-border overflow-hidden transition-all min-h-0",
              !selectedTable && !selectedHistoryPayment && "hidden md:flex",
            )}
          >
            {selectedHistoryPayment ? (
              <>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between px-4 sm:px-6 py-4 border-b border-border gap-3">
                  <div className="flex items-center gap-3">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="md:hidden h-8 w-8"
                      onClick={() => {
                        setSelectedHistoryPayment(null);
                      }}
                    >
                      <ArrowRight className="w-5 h-5 rotate-180" />
                    </Button>
                    <div className="flex flex-col">
                      <h3 className="text-lg sm:text-xl font-bold text-foreground">
                        {selectedHistoryPayment.number_ticket
                          ? "Ticket #" +
                            selectedHistoryPayment.number_ticket +
                            " / "
                          : null}{" "}
                        {selectedHistoryPayment.client_name
                          ? selectedHistoryPayment.client_name
                          : "Mesa " + selectedHistoryPayment.tableNumber}
                      </h3>
                      {!selectedHistoryPayment.client_name &&
                        (() => {
                          const tableObj = selectedHistoryPayment.tableNumber
                            ? tables.find(
                                (t) =>
                                  t.number ===
                                  selectedHistoryPayment.tableNumber,
                              )
                            : null;
                          return tableObj?.zone ? (
                            <span className="text-xs text-muted-foreground font-normal leading-none mt-0.5 mb-1">
                              {tableObj.zone}
                            </span>
                          ) : null;
                        })()}
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Pagada ·{" "}
                        {selectedHistoryPayment.paidAt
                          ? selectedHistoryPayment.paidAt.toLocaleString()
                          : "Sin fecha"}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 w-full sm:w-auto"
                    onClick={() =>
                      handleReceiptReprintLocal(selectedHistoryPayment)
                    }
                  >
                    <Printer className="w-4 h-4" />
                    <span className="text-xs sm:text-sm">Reimprimir</span>
                  </Button>
                </div>

                <div className="flex flex-col xl:flex-row flex-1 overflow-hidden">
                  <div className="flex-1 border-b xl:border-b-0 xl:border-r border-border min-h-0">
                    <div className="px-6 py-3 border-b border-border">
                      <span className="text-sm font-medium text-muted-foreground">
                        Detalle del Pedido
                      </span>
                    </div>
                    <ScrollArea className="h-[calc(100%-49px)]">
                      <div className="p-4 space-y-3">
                        {(selectedHistoryPayment.receipt?.items ?? [])
                          .length === 0 ? (
                          <div className="text-sm text-muted-foreground">
                            Sin detalle
                          </div>
                        ) : (
                          (selectedHistoryPayment.receipt?.items ?? []).map(
                            (item, idx) => (
                              <div
                                key={`${selectedHistoryPayment.id}-${idx}`}
                                className="flex items-center justify-between py-2"
                              >
                                <div className="flex items-center gap-3 min-w-0">
                                  <span className="text-sm font-medium text-emerald-500">
                                    {item.quantity}x
                                  </span>
                                  <span className="text-sm text-foreground truncate">
                                    {item.productName}
                                  </span>
                                </div>
                                <span className="text-sm font-medium text-foreground">
                                  {formatPrice(item.quantity * item.unitPrice)}
                                </span>
                              </div>
                            ),
                          )
                        )}
                      </div>
                    </ScrollArea>
                  </div>

                  <div className="w-full xl:w-80 flex flex-col bg-muted/10 shrink-0">
                    <div className="p-4 space-y-3">
                      <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <span>Subtotal</span>
                        <span className="font-medium text-foreground">
                          {formatPrice(selectedHistoryPayment.subtotal)}
                        </span>
                      </div>
                      {selectedHistoryPayment.discount > 0 && (
                        <div className="flex items-center justify-between text-sm text-muted-foreground">
                          <span>Descuento</span>
                          <span className="font-medium text-foreground">
                            - {formatPrice(selectedHistoryPayment.discount)}
                          </span>
                        </div>
                      )}
                      {selectedHistoryPayment.tip > 0 && (
                        <div className="flex items-center justify-between text-sm text-muted-foreground">
                          <span>Propina</span>
                          <span className="font-medium text-foreground">
                            {formatPrice(selectedHistoryPayment.tip)}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <span>IVA (19%)</span>
                        <span className="font-medium text-foreground">
                          {formatPrice(selectedHistoryPayment.tax)}
                        </span>
                      </div>
                      <div className="pt-2 border-t border-border flex items-center justify-between">
                        <span className="text-sm font-semibold text-muted-foreground">
                          Total
                        </span>
                        <span className="text-lg font-black text-foreground">
                          {formatPrice(selectedHistoryPayment.total)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : !selectedOrder ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <Receipt className="w-16 h-16 text-muted-foreground/30 mb-4" />
                <h3 className="text-lg font-medium text-foreground">
                  Selecciona una mesa
                </h3>
                <p className="text-sm text-muted-foreground">
                  Elige una mesa de la lista para procesar el pago
                </p>
              </div>
            ) : (
              <>
                {/* Order Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between px-4 sm:px-6 py-4 border-b border-border gap-3">
                  <div className="flex items-center gap-3">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="md:hidden h-8 w-8"
                      onClick={() => {
                        setSelectedTable(null);
                        setSelectedOrder(null);
                      }}
                    >
                      <ArrowRight className="w-5 h-5 rotate-180" />
                    </Button>
                    <div className="flex flex-col">
                      <h3 className="text-lg sm:text-xl font-bold text-foreground">
                        {selectedOrder.clientName
                          ? selectedOrder.clientName
                          : `Mesa ${selectedTable?.number}`}
                      </h3>
                      {!selectedOrder.clientName && selectedTable?.zone && (
                        <span className="text-xs text-muted-foreground font-normal leading-none mt-0.5 mb-1">
                          {selectedTable.zone}
                        </span>
                      )}
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Mesero: {selectedOrder.waiter}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 w-full sm:w-auto"
                    onClick={handlePrintPreBill}
                  >
                    <Printer className="w-4 h-4" />
                    <span className="text-xs sm:text-sm">Imprimir cuenta</span>
                  </Button>
                </div>

                <div className="flex flex-col xl:flex-row  xl:overflow-hidden overflow-y-auto min-h-0">
                  {/* Order Items */}
                  <div className="xl:flex-1 flex-row xl:flex xl:flex-col border-b xl:border-b-0 xl:border-r border-border xl:min-h-0">
                    <div className="px-6 py-3 border-b border-border">
                      <span className="text-sm font-medium text-muted-foreground">
                        Detalle del Pedido
                      </span>
                    </div>
                    <div className="flex-1 overflow-y-auto min-h-0 p-4">
                      {selectedOrder.items.map((item) => (
                        <div
                          key={item.id}
                          className="flex items-center justify-between py-2"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-primary">
                              {item.quantity}x
                            </span>
                            <div className="flex flex-col">
                              <span className="text-sm text-foreground">
                                {item.productName}
                              </span>
                              {item.notes && (
                                <span className="text-xs text-amber-500 font-medium italic">
                                  📝 {item.notes}
                                </span>
                              )}
                            </div>
                          </div>
                          <span className="text-sm font-medium text-foreground">
                            {formatPrice(item.quantity * item.unitPrice)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Payment Options */}
                  <div className="w-full xl:w-80 flex flex-col bg-muted/10 shrink-0 min-h-0">
                    <ScrollArea className="flex-1 min-h-0">
                      <div className="p-4 space-y-6">
                        {/* Payment Method */}
                        <div className="space-y-3">
                          <span className="text-sm font-medium text-muted-foreground">
                            Método de Pago
                          </span>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            <button
                              onClick={() => setPaymentMethod("cash")}
                              className={cn(
                                "flex flex-col items-center gap-2 p-3 rounded-lg border transition-colors",
                                paymentMethod === "cash"
                                  ? "bg-primary/10 border-primary text-primary"
                                  : "bg-muted/30 border-border text-muted-foreground hover:text-foreground",
                              )}
                            >
                              <Banknote className="w-5 h-5" />
                              <span className="text-xs font-medium">
                                Efectivo
                              </span>
                            </button>
                            <button
                              onClick={() => setPaymentMethod("card")}
                              className={cn(
                                "flex flex-col items-center gap-2 p-3 rounded-lg border transition-colors",
                                paymentMethod === "card"
                                  ? "bg-primary/10 border-primary text-primary"
                                  : "bg-muted/30 border-border text-muted-foreground hover:text-foreground",
                              )}
                            >
                              <CreditCard className="w-5 h-5" />
                              <span className="text-xs font-medium">
                                Tarjeta
                              </span>
                            </button>
                            <button
                              onClick={() => setPaymentMethod("mixed")}
                              className={cn(
                                "flex flex-col items-center gap-2 p-3 rounded-lg border transition-colors",
                                paymentMethod === "mixed"
                                  ? "bg-primary/10 border-primary text-primary"
                                  : "bg-muted/30 border-border text-muted-foreground hover:text-foreground",
                              )}
                            >
                              <ArrowRight className="w-5 h-5" />
                              <span className="text-xs font-medium">Mixto</span>
                            </button>
                            <button
                              onClick={() => setPaymentMethod("credit")}
                              className={cn(
                                "flex flex-col items-center gap-2 p-3 rounded-lg border transition-colors",
                                paymentMethod === "credit"
                                  ? "bg-violet-500/10 border-violet-500 text-violet-500"
                                  : "bg-muted/30 border-border text-muted-foreground hover:text-foreground",
                              )}
                            >
                              <Receipt className="w-5 h-5" />
                              <span className="text-xs font-medium">
                                Crédito
                              </span>
                            </button>
                          </div>
                        </div>

                        {/* Customer Selection */}
                        <div className="space-y-3">
                          <Form layout="vertical">
                            <ClientSelect
                              onChange={(value, option) => {
                                if (!option?.client) {
                                  setSelectedCustomerId("");
                                  setSelectedClient(null);
                                  setDiscount(0);
                                  return;
                                }
                                setSelectedCustomerId(value.value);
                                setSelectedClient(option.client);
                                setDiscount(
                                  Math.max(
                                    0,
                                    Math.min(
                                      100,
                                      Number(option.client.discount_percent) ||
                                        0,
                                    ),
                                  ),
                                );
                              }}
                            />
                          </Form>

                          {selectedClient && (
                            <div className="p-2.5 rounded-lg bg-muted/40 border border-border text-xs space-y-1">
                              <div className="flex justify-between items-center text-muted-foreground">
                                <span>Crédito Habilitado:</span>
                                <span
                                  className={cn(
                                    "font-medium",
                                    selectedClient.credit_enabled
                                      ? "text-emerald-500"
                                      : "text-red-400",
                                  )}
                                >
                                  {selectedClient.credit_enabled ? "Sí" : "No"}
                                </span>
                              </div>
                              {selectedClient.credit_enabled && (
                                <>
                                  <div className="flex justify-between items-center">
                                    <span className="text-muted-foreground">
                                      Tope de crédito:
                                    </span>
                                    <span className="font-semibold">
                                      {formatPrice(
                                        Number(selectedClient.credit_limit) ||
                                          0,
                                      )}
                                    </span>
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <span className="text-muted-foreground">
                                      Crédito ocupado:
                                    </span>
                                    <span className="font-semibold text-amber-500">
                                      {formatPrice(
                                        Number(selectedClient.credit_balance) ||
                                          0,
                                      )}
                                    </span>
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <span className="text-muted-foreground">
                                      Disponible:
                                    </span>
                                    <span className="font-semibold text-emerald-500">
                                      {formatPrice(
                                        Math.max(
                                          0,
                                          (Number(
                                            selectedClient.credit_limit,
                                          ) || 0) -
                                            (Number(
                                              selectedClient.credit_balance,
                                            ) || 0),
                                        ),
                                      )}
                                    </span>
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Discount */}
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                            <Percent className="w-4 h-4" />
                            <span>Descuento</span>
                          </div>

                          <div className="flex gap-2">
                            {[0, 5, 10, 15].map((d) => (
                              <button
                                key={d}
                                onClick={() => {
                                  setDiscount(d);
                                  setShowCustomDiscount(false);
                                  setCustomDiscount("");
                                }}
                                className={cn(
                                  "flex-1 py-2 rounded-lg text-sm font-medium transition-colors",
                                  discount === d && !showCustomDiscount
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-muted/50 text-muted-foreground hover:text-foreground",
                                )}
                              >
                                {d}%
                              </button>
                            ))}

                            <button
                              onClick={() => {
                                setShowCustomDiscount(true);
                                setDiscount(Number(customDiscount) || 0);
                              }}
                              className={cn(
                                "flex-1 py-2 rounded-lg text-sm font-medium transition-colors",
                                showCustomDiscount
                                  ? "bg-primary text-primary-foreground"
                                  : "bg-muted/50 text-muted-foreground hover:text-foreground",
                              )}
                            >
                              Otro
                            </button>
                          </div>

                          {showCustomDiscount && (
                            <div className="relative">
                              <Input
                                type="number"
                                min={0}
                                max={100}
                                step={1}
                                value={customDiscount}
                                onChange={(e) => {
                                  const value = e.target.value;

                                  if (value === "") {
                                    setCustomDiscount("");
                                    setDiscount(0);
                                    return;
                                  }

                                  const numericValue = Number(value);

                                  if (numericValue > 100) {
                                    setCustomDiscount("100");
                                    setDiscount(100);
                                    return;
                                  }

                                  if (numericValue < 0) {
                                    return;
                                  }

                                  setCustomDiscount(value);
                                  setDiscount(numericValue);
                                }}
                                placeholder="Descuento"
                                className="text-sm pr-10"
                              />

                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                                %
                              </span>
                            </div>
                          )}

                          {discount > 0 && (
                            <Input
                              value={discountCode}
                              onChange={(e) => setDiscountCode(e.target.value)}
                              placeholder="Código / Autorización"
                              className="text-sm"
                            />
                          )}
                        </div>

                        {/* Tip */}
                        {/* Tip */}
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                            <Heart className="w-4 h-4" />
                            <span>Propina</span>
                          </div>

                          <div className="flex gap-2">
                            {tipOptions.map((t) => (
                              <button
                                key={t}
                                onClick={() => {
                                  setTipPercent(t);
                                  setShowCustomTip(false);
                                  setCustomTip("");
                                }}
                                className={cn(
                                  "flex-1 py-2 rounded-lg text-sm font-medium transition-colors",
                                  tipPercent === t && !showCustomTip
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-muted/50 text-muted-foreground hover:text-foreground",
                                )}
                              >
                                {t}%
                              </button>
                            ))}

                            <button
                              onClick={() => {
                                setShowCustomTip(true);
                                setTipPercent(Number(customTip) || 0);
                              }}
                              className={cn(
                                "flex-1 py-2 rounded-lg text-sm font-medium transition-colors",
                                showCustomTip
                                  ? "bg-primary text-primary-foreground"
                                  : "bg-muted/50 text-muted-foreground hover:text-foreground",
                              )}
                            >
                              Otro
                            </button>
                          </div>

                          {showCustomTip && (
                            <div className="relative">
                              <Input
                                type="number"
                                min={0}
                                max={100}
                                step={1}
                                value={customTip}
                                onChange={(e) => {
                                  const value = e.target.value;

                                  if (value === "") {
                                    setCustomTip("");
                                    setTipPercent(0);
                                    return;
                                  }

                                  const numericValue = Number(value);

                                  if (numericValue > 100) {
                                    setCustomTip("100");
                                    setTipPercent(100);
                                    return;
                                  }

                                  if (numericValue < 0) {
                                    return;
                                  }

                                  setCustomTip(value);
                                  setTipPercent(numericValue);
                                }}
                                placeholder="Propina"
                                className="text-sm pr-10"
                              />

                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                                %
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Cash Received (if cash or mixed payment) */}
                        {(paymentMethod === "cash" ||
                          paymentMethod === "mixed") && (
                          <div className="space-y-3">
                            <span className="text-sm font-medium text-muted-foreground">
                              {paymentMethod === "mixed"
                                ? "Efectivo (parte)"
                                : "Efectivo Recibido"}
                            </span>
                            <Input
                              type="number"
                              value={cashReceived}
                              onChange={(e) => setCashReceived(e.target.value)}
                              placeholder="$0"
                              className="text-lg font-semibold"
                            />
                            {paymentMethod === "mixed" && (
                              <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted/50 text-muted-foreground">
                                <span className="text-sm font-medium">
                                  Tarjeta
                                </span>
                                <span className="text-sm font-bold">
                                  {formatPrice(mixedCardAmount)}
                                </span>
                              </div>
                            )}
                            {change > 0 && (
                              <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-emerald-500/10 text-emerald-400">
                                <span className="text-sm font-medium">
                                  Vuelto
                                </span>
                                <span className="text-lg font-bold">
                                  {formatPrice(change)}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </ScrollArea>

                    {/* Summary & Action */}
                    <div className="flex flex-col border-t border-border p-4 space-y-4 shrink-0">
                      <div className="p-4 border-t border-border space-y-4  flex-1 min-h-0">
                        <div className="flex justify-between text-sm text-muted-foreground">
                          <span>Subtotal</span>
                          <span>{formatPrice(subtotal)}</span>
                        </div>
                        {discount > 0 && (
                          <div className="flex justify-between text-sm text-emerald-400">
                            <span>Descuento ({discount}%)</span>
                            <span>-{formatPrice(discountAmount)}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-sm text-muted-foreground">
                          <span>IVA (19%)</span>
                          <span>{formatPrice(tax)}</span>
                        </div>
                        {tip > 0 && (
                          <div className="flex justify-between text-sm text-muted-foreground">
                            <span>Propina ({tipPercent}%)</span>
                            <span>{formatPrice(tip)}</span>
                          </div>
                        )}
                        <Separator />
                        <div className="flex justify-between text-lg font-bold">
                          <span>Total</span>
                          <span className="text-primary">
                            {formatPrice(total)}
                          </span>
                        </div>
                      </div>

                      <Button
                        size="lg"
                        className="w-full gap-2"
                        onClick={handlePayment}
                        disabled={
                          (paymentMethod === "cash" &&
                            (!Number.isFinite(cashReceivedNumber) ||
                              cashReceivedNumber < total)) ||
                          (paymentMethod === "mixed" &&
                            (!Number.isFinite(cashReceivedNumber) ||
                              cashReceivedNumber < 0 ||
                              cashReceivedNumber > total))
                        }
                      >
                        <Check className="w-5 h-5" />
                        Confirmar Pago
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <CreditOverLimitModal
        open={creditOverLimitModalOpen}
        onCancel={() => setCreditOverLimitModalOpen(false)}
        onConfirm={executePaymentProcess}
        selectedClient={selectedClient}
        total={total}
      />
    </>
  );
}
